import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Cake, Download, MessageCircle } from "lucide-react";
import { api, money } from "@/lib/api";
import { formatPhone } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { deliverFile } from "@/lib/pdfFiling";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

const fill = (template, name) =>
  (template || "").replaceAll("{name}", name || "there");

export const Birthdays = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    api
      .get("/settings/birthday-template")
      .then(({ data: d }) => setTemplate(d.message || ""))
      .catch(() => setTemplate(""));
  }, []);

  const saveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await api.put("/settings/birthday-template", { message: template });
      toast.success("Birthday wording saved for everyone");
    } catch {
      toast.error("Could not save the wording");
    } finally {
      setSavingTemplate(false);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    setData(null);
    api
      .get("/customers/birthdays", { params: { month } })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error("Could not load the birthday list"))
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(load, [load]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/customers/birthdays.csv", {
        params: { month },
        responseType: "blob",
      });
      await deliverFile(
        res.data,
        "Reports",
        `birthdays-${MONTHS[month - 1].toLowerCase()}.csv`,
        toast
      );
    } catch {
      toast.error("Could not download the list");
    }
  };

  const wish = (row) => {
    const digits = (row.phone || "").replace(/\D/g, "");
    if (!digits) return toast.error("No phone number on this customer");
    const intl = digits.startsWith("61") ? digits : `61${digits.replace(/^0+/, "")}`;
    const text = fill(template, row.name);
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const copyMessage = async (row) => {
    await navigator.clipboard.writeText(fill(template, row.name));
    toast.success(`Message for ${row.name || "customer"} copied`);
  };

  const copyEmails = async () => {
    const emails = (data?.rows || []).map((r) => r.email).filter(Boolean).join(", ");
    if (!emails) return toast.error("Nobody in this month has an email address on file");
    await navigator.clipboard.writeText(emails);
    toast.success(`${emails.split(", ").length} email address(es) copied`);
  };

  const copyNumbers = async () => {
    const numbers = (data?.rows || []).map((r) => r.phone).filter(Boolean).join(", ");
    if (!numbers) return toast.error("No numbers to copy");
    await navigator.clipboard.writeText(numbers);
    toast.success(`${data.rows.length} number(s) copied`);
  };

  const t = data?.totals;

  return (
    <div data-testid="birthdays-tab">
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {MONTHS.map((name, i) => (
          <button
            key={name}
            onClick={() => setMonth(i + 1)}
            data-testid={`birthday-month-${i + 1}`}
            className={`border px-3 py-1.5 text-xs uppercase tracking-wider transition-colors duration-150 ${
              month === i + 1
                ? "border-[#0A0A0A] bg-[#0A0A0A] font-semibold text-white"
                : "border-border bg-white hover:bg-[#FBF8F2]"
            }`}
          >
            {name.slice(0, 3)}
          </button>
        ))}
      </div>

      <div className="mt-5 border border-border p-4" data-testid="birthday-template-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider">
              Your birthday offer wording
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Change it whenever the offer changes — every WhatsApp and copied message
              uses this. Write <code>{"{name}"}</code> where the customer's name should go.
            </p>
          </div>
          <Button
            variant="outline"
            className="h-9 rounded-none"
            onClick={saveTemplate}
            disabled={savingTemplate || !template.trim()}
            data-testid="birthday-template-save-button"
          >
            {savingTemplate ? "Saving…" : "Save wording"}
          </Button>
        </div>
        <textarea
          className="mt-3 h-24 w-full border border-border p-3 text-sm"
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          data-testid="birthday-template-input"
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border border-border bg-[#FBF8F2] px-4 py-3">
        <p className="text-sm" data-testid="birthday-summary">
          <strong>{t?.count ?? 0}</strong> customer(s) have a birthday in{" "}
          {data?.month_name || MONTHS[month - 1]}
          {t?.today ? ` — ${t.today} today` : ""}
          {t?.with_email ? `, ${t.with_email} with an email address` : ""}.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="h-9 rounded-none"
            onClick={copyNumbers}
            data-testid="birthday-copy-numbers-button"
          >
            Copy all numbers
          </Button>
          <Button
            variant="outline"
            className="h-9 rounded-none"
            onClick={copyEmails}
            data-testid="birthday-copy-emails-button"
          >
            Copy all emails
          </Button>
          <Button
            variant="outline"
            className="h-9 rounded-none"
            onClick={downloadCsv}
            data-testid="birthday-csv-button"
          >
            <Download className="mr-1.5 h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto border border-border">
        <table className="w-full min-w-[760px] text-sm" data-testid="birthdays-table">
          <thead className="bg-[#0A0A0A] text-left text-white">
            <tr>
              <th className="px-3 py-2.5 font-semibold">Day</th>
              <th className="px-3 py-2.5 font-semibold">Customer</th>
              <th className="px-3 py-2.5 font-semibold">Phone</th>
              <th className="px-3 py-2.5 font-semibold">Email</th>
              <th className="px-3 py-2.5 text-right font-semibold">Spent with us</th>
              <th className="px-3 py-2.5 text-right font-semibold">Offer</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(!data?.rows || data.rows.length === 0) && (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center text-muted-foreground">
                  {loading
                    ? "Loading…"
                    : `Nobody on file has a birthday in ${
                        data?.month_name || MONTHS[month - 1]
                      }. Birthdays are collected when a customer registers at the counter.`}
                </td>
              </tr>
            )}
            {(data?.rows || []).map((r) => (
              <tr
                key={r.phone}
                className={r.is_today ? "bg-[#FFFBEB]" : "hover:bg-[#FBF8F2]"}
                data-testid={`birthday-row-${r.phone}`}
              >
                <td className="tabular px-3 py-2.5 font-semibold">
                  {r.day}
                  {r.is_today && (
                    <span className="ml-2 border border-[#C5A059] px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[#8A6C36]">
                      today
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <Link
                    to={`/customers/${r.phone}`}
                    className="font-semibold hover:underline"
                  >
                    {r.name || "No name"}
                  </Link>
                  {r.turning && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      turning {r.turning}
                    </span>
                  )}
                </td>
                <td className="tabular px-3 py-2.5">{formatPhone(r.phone)}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">
                  {r.email || "—"}
                </td>
                <td className="tabular px-3 py-2.5 text-right">{money(r.spent)}</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button
                      variant="outline"
                      className="h-8 rounded-none border-[#1F6F43] text-xs text-[#1F6F43] hover:bg-[#F1FAF4]"
                      onClick={() => wish(r)}
                      data-testid={`birthday-whatsapp-${r.phone}`}
                    >
                      <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                    </Button>
                    <Button
                      variant="outline"
                      className="h-8 rounded-none text-xs"
                      onClick={() => copyMessage(r)}
                      data-testid={`birthday-copy-${r.phone}`}
                    >
                      Copy
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
        <Cake className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Nothing is sent automatically. The portal cannot send a marketing blast by email —
        use Copy all numbers with a WhatsApp Broadcast list, or Copy all emails to send
        the offer from the shop's own email account.
      </p>
    </div>
  );
};
